/**
 * useBranchCartCheck: the cart check at a branch, and the review it opens.
 * ---------------------------------------------------------------------------
 * Branch operations (4 Oct 2026). A branch may have an item sold out,
 * switched off or priced differently, so a cart is checked at the location
 * it is fulfilled from before the shopper pays. THE one call site for
 * POST /api/catalog/branch-quote on the website; the brand, restaurant and
 * healthcare carts and checkouts all go through it.
 *
 *   ensureChecked()        asks the server only when a line is unconfirmed
 *                          (utils/branchCart). Resolves true to go on, false
 *                          when the review dialog opened. A failed check (no
 *                          signal, or a server that predates branches)
 *                          resolves true: the order-create gate is the
 *                          authority and refuses what it must.
 *   guardSubmit(run)       wraps a checkout's create-order call: checks
 *                          first, and a 409 BRANCH_LINES_CHANGED from the
 *                          server opens the same dialog from its `lines`.
 *   handleOrderRefusal(p)  the same, for a refusal read elsewhere.
 *   reviewNextCheck()      the next automatic check opens the dialog (after
 *                          the cart moved to another branch).
 *   dialogProps            spread onto components/branch/BranchCartReviewDialog
 *
 * `checkWhen` runs the check when it turns true (a cart opening, a page
 * loading) and again whenever more lines need it (a live branch change, a
 * cart read back from localStorage). With `silent` that automatic check only
 * marks the lines, so a storefront does not interrupt browsing; the cart and
 * Place order then show the review.
 *
 * `useCartBranchCheck` below is the same hook bound to a vertical's cart in
 * the store, so a page names the vertical and the cart and nothing else.
 * Same contract as the apps' app/hooks/useBranchCartCheck.js.
 * ---------------------------------------------------------------------------
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { userRequest } from "../api/requestMethods";
import { formatCurrency } from "../config/market";
import {
  LINE_STATUS,
  branchRefusalOf,
  diffCartAgainst,
} from "../utils/branchAvailability";
import {
  cartLinesOf,
  cartLocationOf,
  cartNeedsCheck,
  toQuoteLine,
} from "../utils/branchCart";
import {
  acceptBranchPrice as acceptBrandPrice,
  applyBranchQuote as applyBrandQuote,
  removeFromCart as removeBrandLine,
} from "../redux/brandCartSlice";
import {
  acceptBranchPrice as acceptRestaurantPrice,
  applyBranchQuote as applyRestaurantQuote,
  removeFromCart as removeRestaurantLine,
} from "../redux/restaurantCartSlice";
import {
  acceptBranchPrice as acceptHealthcarePrice,
  applyBranchQuote as applyHealthcareQuote,
  removeFromCart as removeHealthcareLine,
} from "../redux/healthcareCartSlice";

export const BRANCH_QUOTE_URL = "/api/catalog/branch-quote";

/** What guardSubmit hands back when it stopped for the review dialog. */
export const BRANCH_REVIEW_OPEN = "BRANCH_REVIEW_OPEN";
const stoppedAction = () => ({
  type: "branch/reviewOpen",
  error: { message: "Review your cart to continue." },
  payload: { code: BRANCH_REVIEW_OPEN },
  meta: { requestStatus: "rejected" },
});

const finite = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

/**
 * @param {object}   params
 * @param {"brand"|"restaurant"|"healthcare"} params.vertical
 * @param {string}   params.vendorId      the vendor document id or its owner id
 * @param {object|object[]} params.lines  the cart (map or array)
 * @param {string}   [params.locationName] the location's name for the dialog
 * @param {function} params.onQuote       (verdicts) => void
 * @param {function} params.onRemove      (productId) => void
 * @param {function} params.onKeep        (productId) => void
 * @param {boolean}  [params.checkWhen=false]
 * @param {boolean}  [params.silent=false]
 * @param {function} [params.formatMoney=formatCurrency]
 */
const useBranchCartCheck = ({
  vertical,
  vendorId,
  lines,
  locationName = "",
  onQuote,
  onRemove,
  onKeep,
  checkWhen = false,
  silent = false,
  formatMoney = formatCurrency,
}) => {
  const [review, setReview] = useState(null);
  const [checking, setChecking] = useState(false);
  const inFlightRef = useRef(null);
  const reviewNextRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // Latest values for the async paths, so a stale closure never quotes the
  // cart as it was two clicks ago.
  const latest = useRef({});
  latest.current = {
    vertical,
    vendorId,
    lines: cartLinesOf(lines),
    locationName,
    onQuote,
    onRemove,
    onKeep,
    formatMoney,
  };

  const openReview = useCallback((verdicts, { branchName, subtotal } = {}) => {
    const { lines: current, formatMoney: money } = latest.current;
    const items = diffCartAgainst(current, verdicts, { branchName, formatMoney: money });
    if (!mountedRef.current) return items.length > 0;
    if (!items.length) {
      setReview(null);
      return false;
    }
    setReview({
      branchName,
      items: items.map((item) => ({ ...item, resolved: null })),
      subtotal: finite(subtotal) ? Number(subtotal) : null,
    });
    return true;
  }, []);

  /* One request at a time, shared by every caller; each caller then decides
     whether to open the dialog. Resolves the answer, or null when there is
     nothing to say (empty cart, failed check). */
  const runCheck = useCallback(() => {
    if (inFlightRef.current) return inFlightRef.current;
    const { vertical: v, vendorId: id, lines: current } = latest.current;
    if (!v || !id || !current.length) return Promise.resolve(null);
    setChecking(true);
    inFlightRef.current = userRequest
      .post(BRANCH_QUOTE_URL, {
        vertical: v,
        vendorId: String(id),
        location: cartLocationOf(current),
        lines: current.map(toQuoteLine),
      })
      .then((res) => {
        const verdicts = Array.isArray(res?.data?.lines) ? res.data.lines : null;
        if (!verdicts) return null;
        const apply = latest.current.onQuote;
        if (typeof apply === "function") apply(verdicts);
        return {
          verdicts,
          branchName: res.data?.location?.name || latest.current.locationName,
          subtotal: res.data?.subtotal,
        };
      })
      // A failed check never blocks the order: the server gate decides.
      .catch(() => null)
      .finally(() => {
        inFlightRef.current = null;
        if (mountedRef.current) setChecking(false);
      });
    return inFlightRef.current;
  }, []);

  const ensureChecked = useCallback(
    async ({ force = false, silent: quiet = false } = {}) => {
      if (!force && !cartNeedsCheck(latest.current.lines)) return true;
      const answer = await runCheck();
      if (!answer) return true;
      if (quiet) return true;
      return !openReview(answer.verdicts, answer);
    },
    [runCheck, openReview]
  );

  const reviewNextCheck = useCallback(() => {
    reviewNextRef.current = true;
  }, []);

  /* The automatic check: when `checkWhen` turns true, and again when MORE
     lines need confirming or the cart moved (a move, a live change, a stored
     cart). Never while the dialog is open, and never because lines were just
     resolved in it: that would re-ask the question being answered. */
  const pending = cartLinesOf(lines).filter((line) => line.needsCheck !== false).length;
  const where = cartLocationOf(lines);
  const lastAutoRef = useRef({ on: false, pending: 0, where: null });
  const reviewOpen = Boolean(review);
  useEffect(() => {
    const last = lastAutoRef.current;
    lastAutoRef.current = { on: checkWhen, pending, where };
    if (!checkWhen || !pending || reviewOpen) return;
    const due = !last.on || pending > last.pending || where !== last.where;
    if (!due) return;
    const loud = reviewNextRef.current;
    reviewNextRef.current = false;
    ensureChecked({ silent: silent && !loud });
  }, [checkWhen, pending, where, silent, reviewOpen, ensureChecked]);

  const handleOrderRefusal = useCallback(
    (payload) => {
      const refusal = branchRefusalOf(payload);
      if (!refusal) return false;
      const apply = latest.current.onQuote;
      if (typeof apply === "function") apply(refusal.lines);
      return openReview(refusal.lines, {
        branchName: refusal.location?.name || latest.current.locationName,
      });
    },
    [openReview]
  );

  /* The create-order call, checked first. `run` resolves the dispatched
     thunk's action, as every checkout's submit already does, so the callers
     keep their own `action.error` handling. */
  const guardSubmit = useCallback(
    async (run) => {
      const go = await ensureChecked();
      if (!go) return stoppedAction();
      const action = await run();
      if (action?.error) handleOrderRefusal(action.payload);
      return action;
    },
    [ensureChecked, handleOrderRefusal]
  );

  const resolve = useCallback((productId, how) => {
    setReview((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((item) =>
              item.productId === String(productId) ? { ...item, resolved: how } : item
            ),
          }
        : prev
    );
  }, []);

  const removeLine = useCallback(
    (productId) => {
      const remove = latest.current.onRemove;
      if (typeof remove === "function") remove(productId);
      resolve(productId, "removed");
    },
    [resolve]
  );

  const keepLine = useCallback(
    (productId) => {
      const keep = latest.current.onKeep;
      if (typeof keep === "function") keep(productId);
      resolve(productId, "kept");
    },
    [resolve]
  );

  const closeReview = useCallback(() => setReview(null), []);

  // The server's subtotal, less any repriced line the shopper then removed
  // (the lines it could not supply were never in it).
  const reviewView = useMemo(() => {
    if (!review) return null;
    const removedTotal = review.items.reduce(
      (sum, item) =>
        item.resolved === "removed" &&
        item.status === LINE_STATUS.PRICE_CHANGED &&
        finite(item.lineTotal)
          ? sum + Number(item.lineTotal)
          : sum,
      0
    );
    return {
      ...review,
      subtotal: review.subtotal === null ? null : Math.max(0, review.subtotal - removedTotal),
      allResolved: review.items.every((item) => Boolean(item.resolved)),
    };
  }, [review]);

  return {
    checking,
    review: reviewView,
    ensureChecked,
    guardSubmit,
    handleOrderRefusal,
    reviewNextCheck,
    closeReview,
    dialogProps: {
      review: reviewView,
      onRemove: removeLine,
      onKeep: keepLine,
      onClose: closeReview,
      formatMoney,
    },
  };
};

/* Each vertical's cart in the store: where it lives and the three things the
   check does to it. One table, so the pages never wire the actions by hand. */
const CART_STORE = {
  brand: {
    select: (state, cartId) => state.brandCart?.carts?.[cartId],
    apply: (cartId, verdicts) => applyBrandQuote({ cartId, verdicts }),
    keep: (cartId, productId) => acceptBrandPrice({ cartId, productId }),
    remove: (cartId, productId) => removeBrandLine({ brandId: cartId, productId }),
  },
  restaurant: {
    select: (state, cartId) => state.restaurantCart?.carts?.[cartId],
    apply: (cartId, verdicts) => applyRestaurantQuote({ cartId, verdicts }),
    keep: (cartId, productId) => acceptRestaurantPrice({ cartId, productId }),
    remove: (cartId, productId) => removeRestaurantLine({ restaurantId: cartId, productId }),
  },
  healthcare: {
    select: (state, cartId) => state.healthcareCart?.carts?.[cartId],
    apply: (cartId, verdicts) => applyHealthcareQuote({ cartId, verdicts }),
    keep: (cartId, productId) => acceptHealthcarePrice({ cartId, productId }),
    remove: (cartId, productId) => removeHealthcareLine({ vendorId: cartId, productId }),
  },
};

const EMPTY = Object.freeze({});

/**
 * useBranchCartCheck bound to one vertical's cart in the store.
 *
 * @param {object} params
 * @param {"brand"|"restaurant"|"healthcare"} params.vertical
 * @param {string}  params.cartId        the key the cart is stored under
 * @param {string}  [params.vendorId]    the id to quote with (defaults to cartId)
 * @param {string}  [params.locationName]
 * @param {boolean} [params.checkWhen]
 * @param {boolean} [params.silent]
 */
export const useCartBranchCheck = ({
  vertical,
  cartId,
  vendorId,
  locationName,
  checkWhen = false,
  silent = false,
}) => {
  const dispatch = useDispatch();
  const store = CART_STORE[vertical] || null;
  const cart = useSelector((state) =>
    store && cartId ? store.select(state, cartId) || EMPTY : EMPTY
  );
  return useBranchCartCheck({
    vertical,
    vendorId: vendorId || cartId,
    lines: cart,
    locationName,
    checkWhen: Boolean(store && cartId) && checkWhen,
    silent,
    onQuote: (verdicts) => store && cartId && dispatch(store.apply(cartId, verdicts)),
    onRemove: (productId) => store && cartId && dispatch(store.remove(cartId, productId)),
    onKeep: (productId) => store && cartId && dispatch(store.keep(cartId, productId)),
  });
};

export default useBranchCartCheck;
